import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, of } from 'rxjs';
import {
  YdsButtonComponent,
  YdsCardComponent,
  YdsDropdownComponent,
  YdsEmptyStatesComponent,
  YdsSpinnerComponent,
  YdsTableCellDirective,
  YdsTableComponent,
  type YdsDropdownOption,
  type YdsTableColumn,
} from '../../../yds-angular';
import { formatDisplayDate } from '../../../shared/general-ledger-yds.utils';
import {
  BookingGroupOption,
  ConsolidationDefinitionOption,
  InstitutionOption,
  JournalOverviewRow,
  OverviewDefinitionOption,
  OverviewJournalEntryService,
} from './overviewjournalentry.service';

@Component({
  selector: 'app-overviewjournalentry',
  imports: [
    YdsCardComponent,
    YdsDropdownComponent,
    YdsButtonComponent,
    YdsTableComponent,
    YdsTableCellDirective,
    YdsSpinnerComponent,
    YdsEmptyStatesComponent,
  ],
  templateUrl: './overviewjournalentry.component.html',
  styleUrl: './overviewjournalentry.component.scss',
})
export class OverviewJournalEntryComponent implements OnInit {
  private service = inject(OverviewJournalEntryService);
  private destroyRef = inject(DestroyRef);
  private readonly fastCreateSpinnerMs = 400;
  private readonly institutionRetryMs = 15000;

  readonly tableColumns: YdsTableColumn[] = [
    { key: 'institution_Name', header: 'Bedrijfsnaam' },
    { key: 'journal_Type', header: 'Afr.per' },
    { key: 'payrollProcessingType', header: 'Soort salarisverwerking' },
    { key: 'productionDate', header: 'Prod.datum' },
    { key: 'journalingType', header: 'Soort journalisering' },
  ];

  // Data sources
  private institutions = signal<InstitutionOption[]>([]);
  private bookingGroups = signal<BookingGroupOption[]>([]);
  private overviewDefinitions = signal<OverviewDefinitionOption[]>([]);
  private consolidationDefinitions = signal<ConsolidationDefinitionOption[]>([]);

  // Selected values
  institutionId = signal<string | null>(null);
  bookingGroupId = signal<string | null>(null);
  overviewDefinitionId = signal<string | null>(null);
  consolidationDefinitionId = signal<string | null>(null);

  // Loading flags
  isLoadingDefinitions = signal(false);
  isLoadingResults = signal(false);
  isCreatingJournalFile = signal(false);
  hasSearched = signal(false);

  // Results
  rows = signal<JournalOverviewRow[]>([]);
  selectedIds = signal<readonly unknown[]>([]);

  private lastQueryKey: string | null = null;
  private institutionRetryTimerId: number | null = null;

  // ---------------------------------------------------------------------------
  // Select options

  institutionOptions = computed<YdsDropdownOption[]>(() =>
    this.institutions().map((item) => ({
      id: item.institution_ID,
      label: `${item.payrollClient_Name}/${item.institution_Name}`,
    })),
  );

  bookingGroupOptions = computed<YdsDropdownOption[]>(() =>
    this.bookingGroups().map((item) => ({
      id: item.booking_Group_ID,
      label: item.bookingGroup_Name,
    })),
  );

  overviewDefinitionOptions = computed<YdsDropdownOption[]>(() =>
    this.overviewDefinitions().map((item) => ({
      id: item.overview_Definition_ID,
      label: item.overviewDefinition_Name,
    })),
  );

  consolidationDefinitionOptions = computed<YdsDropdownOption[]>(() =>
    this.consolidationDefinitions().map((item) => ({
      id: item.consolidation_Definition_ID,
      label: item.consolidationDefinition_Name,
    })),
  );

  hasInstitution = computed(() => !!this.institutionId());
  hasBookingGroup = computed(() => !!this.bookingGroupId());
  hasSelectedRows = computed(() => this.selectedIds().length > 0);

  // Table rows mapped to display values
  tableItems = computed(() =>
    this.rows().map((row) => ({
      id: row.journal_PlanningRecord_ID,
      institution_Name: row.institution_Name,
      journal_Type: row.journal_Type,
      payrollProcessingType: 'Vervolg verwerking met afsluiting',
      productionDate: formatDisplayDate(row.changeDateTime, 'nl-NL'),
      journalingType: 'Vervanging',
    })),
  );

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.clearInstitutionRetry();
    });

    // Fire the journal overview search whenever all required fields are set.
    effect(() => {
      const institutionId = this.institutionId();
      const bookingGroupId = this.bookingGroupId();
      const overviewDefinitionId = this.overviewDefinitionId();
      const consolidationDefinitionId = this.consolidationDefinitionId();
      const normalizedConsolidationDefinitionId = consolidationDefinitionId ?? '';

      if (!institutionId || !bookingGroupId || !overviewDefinitionId) {
        this.lastQueryKey = null;
        this.isLoadingResults.set(false);
        this.hasSearched.set(false);
        this.rows.set([]);
        this.selectedIds.set([]);
        return;
      }

      const key = [
        institutionId,
        bookingGroupId,
        overviewDefinitionId,
        normalizedConsolidationDefinitionId,
      ].join('|');
      if (key === this.lastQueryKey) {
        return;
      }
      this.lastQueryKey = key;

      this.loadJournalOverview({
        institutionId,
        bookingGroupId,
        overviewDefinitionId,
        consolidationDefinitionId: normalizedConsolidationDefinitionId,
      });
    });
  }

  ngOnInit(): void {
    this.loadInstitutions();

    this.loadBookingGroups();
  }

  private loadInstitutions(): void {
    this.service
      .getInstitutions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (items) => {
          this.institutions.set(items);
          this.clearInstitutionRetry();
        },
        error: () => {
          this.institutions.set([]);
          this.scheduleInstitutionRetry();
        },
      });
  }

  private scheduleInstitutionRetry(): void {
    if (this.institutionRetryTimerId !== null) {
      return;
    }

    this.institutionRetryTimerId = window.setTimeout(() => {
      this.institutionRetryTimerId = null;
      this.loadInstitutions();
    }, this.institutionRetryMs);
  }

  private clearInstitutionRetry(): void {
    if (this.institutionRetryTimerId === null) {
      return;
    }

    window.clearTimeout(this.institutionRetryTimerId);
    this.institutionRetryTimerId = null;
  }

  private loadBookingGroups(): void {
    this.service
      .getBookingGroups()
      .pipe(
        catchError(() => of([] as BookingGroupOption[])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((items) => {
        this.bookingGroups.set(items);
        if (items.length > 0) {
          // Default select the first booking group and load its definitions.
          this.onBookingGroupChange(items[0].booking_Group_ID);
        }
      });
  }

  onInstitutionChange(value: string | null): void {
    this.institutionId.set(value);

    // Reset dependent fields and refresh the booking groups (and their
    // overview/consolidation definitions) for the newly selected company.
    this.bookingGroupId.set(null);
    this.overviewDefinitionId.set(null);
    this.consolidationDefinitionId.set(null);
    this.bookingGroups.set([]);
    this.overviewDefinitions.set([]);
    this.consolidationDefinitions.set([]);
    this.resetResults();

    if (!value) {
      return;
    }

    this.loadBookingGroups();
  }

  onBookingGroupChange(value: string | null): void {
    this.bookingGroupId.set(value);

    // Reset dependent fields.
    this.overviewDefinitionId.set(null);
    this.consolidationDefinitionId.set(null);
    this.overviewDefinitions.set([]);
    this.consolidationDefinitions.set([]);
    this.resetResults();

    if (!value) {
      return;
    }

    this.isLoadingDefinitions.set(true);

    this.service
      .getOverviewDefinitions(value)
      .pipe(
        catchError(() => of([] as OverviewDefinitionOption[])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((items) => this.overviewDefinitions.set(items));

    this.service
      .getConsolidationDefinitions(value)
      .pipe(
        catchError(() => of([] as ConsolidationDefinitionOption[])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((items) => {
        this.consolidationDefinitions.set(items);
        this.consolidationDefinitionId.set(null);
        this.isLoadingDefinitions.set(false);
      });
  }

  onOverviewDefinitionChange(value: string | null): void {
    this.overviewDefinitionId.set(value);
    this.resetResults();
  }

  onConsolidationDefinitionChange(value: string | null): void {
    this.consolidationDefinitionId.set(value);
  }

  onTableRowClick(item: Record<string, unknown>): void {
    const rowId = item['id'];
    if (rowId === undefined || rowId === null) {
      return;
    }

    const current = [...this.selectedIds()];
    const index = current.indexOf(rowId);

    if (index >= 0) {
      current.splice(index, 1);
    } else {
      current.push(rowId);
    }

    this.selectedIds.set(current);
  }

  private resetResults(): void {
    this.lastQueryKey = null;
    this.isLoadingResults.set(false);
    this.hasSearched.set(false);
    this.rows.set([]);
    this.selectedIds.set([]);
  }

  private loadJournalOverview(query: {
    institutionId: string;
    bookingGroupId: string;
    overviewDefinitionId: string;
    consolidationDefinitionId: string;
  }): void {
    this.isLoadingResults.set(true);
    this.hasSearched.set(true);
    this.selectedIds.set([]);

    this.service
      .getJournalOverview(query)
      .pipe(
        catchError(() => of([] as JournalOverviewRow[])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((items) => {
        this.rows.set(items);
        this.isLoadingResults.set(false);
      });
  }

  createJournalFile(): void {
    const institutionId = this.institutionId();
    const bookingGroupId = this.bookingGroupId();
    const overviewDefinitionId = this.overviewDefinitionId();
    const consolidationDefinitionId = this.consolidationDefinitionId();
    const normalizedConsolidationDefinitionId = consolidationDefinitionId ?? '';

    if (
      !institutionId ||
      !bookingGroupId ||
      !overviewDefinitionId ||
      this.selectedIds().length === 0 ||
      this.isCreatingJournalFile()
    ) {
      return;
    }

    this.isCreatingJournalFile.set(true);
    const selectedPlanningRecordIds = this.selectedIds().map((id) => String(id));

    // Defer the request one tick so the spinner can render before fast download
    // responses start blocking UI feedback.
    window.setTimeout(() => {
      const startedAt = Date.now();

      this.service
        .createJournalFile(
          {
            institutionId,
            bookingGroupId,
            overviewDefinitionId,
            consolidationDefinitionId: normalizedConsolidationDefinitionId,
          },
          selectedPlanningRecordIds,
        )
        .pipe(
          catchError(() => of(void 0)),
          finalize(() => {
            const elapsedMs = Date.now() - startedAt;
            // Keep quick downloads from flickering by showing the spinner briefly,
            // but do not exceed the fast-spinner cap.
            const remainingMs = Math.max(0, this.fastCreateSpinnerMs - elapsedMs);

            if (remainingMs === 0) {
              this.isCreatingJournalFile.set(false);
              return;
            }

            window.setTimeout(() => {
              this.isCreatingJournalFile.set(false);
            }, remainingMs);
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }, 0);
  }
}
